import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Scissors, Droplets } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const firstName = (b) => (b.first_name || b.customer_name || "").split(" ")[0] || "?";

// Shared month calendar: each day shows its bookings (time + first name),
// colored by type — grooming (brand) / self-service (bamboo).
export const MonthCalendar = ({ grooming = [], selfwash = [], onDaySelect, selectedDate }) => {
  const { t, lang } = useLang();
  const mc = t.admin.month_cal;
  const todayStr = useMemo(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
  }, []);
  const [cursor, setCursor] = useState(() => new Date());

  const byDay = useMemo(() => {
    const map = {};
    const push = (dateStr, entry) => {
      if (!dateStr) return;
      (map[dateStr] = map[dateStr] || []).push(entry);
    };
    grooming.filter((b) => b.status !== "cancelled").forEach((b) => push(b.preferred_date, { type: "grooming", time: b.preferred_time, label: firstName(b), booking: b }));
    selfwash.filter((b) => b.status !== "cancelled").forEach((b) => push(b.date, { type: "selfwash", time: b.time, label: firstName(b), booking: b }));
    Object.values(map).forEach((arr) => arr.sort((a, b2) => (a.time || "").localeCompare(b2.time || "")));
    return map;
  }, [grooming, selfwash]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthLabel = new Intl.DateTimeFormat(lang === "el" ? "el-GR" : "en-GB", { month: "long", year: "numeric" }).format(cursor);
  const weekDays = mc.weekdays;

  return (
    <div className="rounded-2xl bg-white border border-brand/10 p-4 sm:p-5 shadow-[0_8px_30px_rgba(134,77,99,0.06)]" data-testid="month-calendar">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          className="grid place-items-center h-9 w-9 rounded-full bg-brand-soft/50 text-brand hover:bg-brand-soft transition-colors"
          data-testid="cal-prev-month"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <p className="font-heading text-lg font-bold text-brand-ink capitalize" data-testid="cal-month-label">{monthLabel}</p>
        <button
          type="button"
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          className="grid place-items-center h-9 w-9 rounded-full bg-brand-soft/50 text-brand hover:bg-brand-soft transition-colors"
          data-testid="cal-next-month"
          aria-label="Next month"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold text-brand-ink/50 mb-1">
        {weekDays.map((d) => <span key={d}>{d}</span>)}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const entries = byDay[dateStr] || [];
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDate;
          return (
            <button
              key={dateStr}
              type="button"
              onClick={() => onDaySelect?.(dateStr)}
              className={`min-h-[64px] sm:min-h-[76px] rounded-xl border p-1 text-left align-top transition-colors ${
                isSelected ? "border-brand bg-brand-soft/40" : isToday ? "border-brand/50 bg-brand-soft/20" : "border-brand/10 bg-stone-alt/40 hover:bg-brand-soft/30"
              }`}
              data-testid={`cal-day-${dateStr}`}
            >
              <span className={`text-[11px] font-bold ${isToday ? "text-brand" : "text-brand-ink/60"}`}>{d}</span>
              <span className="mt-0.5 flex flex-col gap-0.5">
                {entries.slice(0, 2).map((e, j) => (
                  <span
                    key={j}
                    className={`inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-[10px] font-bold leading-tight truncate ${
                      e.type === "grooming" ? "bg-brand/15 text-brand" : "bg-bamboo-soft text-bamboo-deep"
                    }`}
                  >
                    {e.type === "grooming" ? <Scissors className="h-2.5 w-2.5 shrink-0" /> : <Droplets className="h-2.5 w-2.5 shrink-0" />}
                    {e.time} {e.label}
                  </span>
                ))}
                {entries.length > 2 && (
                  <span className="text-[10px] font-bold text-brand-ink/50 px-1">+{entries.length - 2}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MonthCalendar;
